// One sign-in page for everyone. After the password (and a 2FA code if they've turned it on) people go
// straight to their area, or choose one if they have more than one.
import { useState, type FormEvent } from 'react'
import type { LoginResult } from '@briary/shared'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { Link, useRouter } from '../../lib/router'
import { Button, Notice, TextField } from '../../ui'
import { AuthLayout } from './AuthLayout'
import { PortalAccessRequestModal } from '../../modules/sales/PortalRequestForms'

export default function Login() {
  const { setMe } = useAuth()
  const { navigate, loc } = useRouter()
  const [step, setStep] = useState<'password' | 'mfa'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [requestingAccess, setRequestingAccess] = useState(false)
  const reset = loc.search.get('reset') === '1'

  const finish = (r: LoginResult) => {
    if (r.status === 'mfa') {
      setStep('mfa')
      setCode('')
      return
    }
    setMe(r.me)
    const back = loc.search.get('next')
    navigate(back && back.startsWith('/') && !back.startsWith('//') ? back : r.next, { replace: true })
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (step === 'password') finish(await api.post<LoginResult>('/api/auth/login', { email, password }))
      else finish(await api.post<LoginResult>('/api/auth/mfa', { code }))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.')
      if (err instanceof ApiError && err.message.startsWith('That took too long')) setStep('password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout title="Sign in">
      {step === 'password' ? (
        <form onSubmit={submit} noValidate>
          <h2>Sign in</h2>
          <p className="lead">Use the email and password you use for Briary Hub today.</p>
          {reset && <Notice tone="good">Your password has been changed. Sign in with the new one.</Notice>}
          {error && <Notice tone="bad">{error}</Notice>}
          <TextField label="Email" type="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          <Button type="submit" variant="primary" size="lg" block busy={busy}>
            Sign in
          </Button>
          <div className="auth-links">
            <Link to="/forgot">Forgotten your password?</Link>
            <Button variant="ghost" onClick={() => setRequestingAccess(true)}>Request portal access</Button>
          </div>
        </form>
      ) : (
        <form onSubmit={submit} noValidate>
          <h2>Two-step verification</h2>
          <p className="lead">
            {useRecovery ? 'Enter one of the recovery codes you saved when you set up two-step verification.' : 'Enter the 6-digit code from your authenticator app.'}
          </p>
          {error && <Notice tone="bad">{error}</Notice>}
          <TextField
            label={useRecovery ? 'Recovery code' : 'Code'}
            className={useRecovery ? '' : 'input-code'}
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            maxLength={useRecovery ? 12 : 6}
            value={code}
            onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ''))}
          />
          <Button type="submit" variant="primary" size="lg" block busy={busy}>
            Continue
          </Button>
          <div className="auth-links">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setUseRecovery((r) => !r)
                setCode('')
                setError(null)
              }}
            >
              {useRecovery ? 'Use my authenticator app' : 'Lost your phone? Use a recovery code'}
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setStep('password')
                setError(null)
              }}
            >
              Back
            </a>
          </div>
        </form>
      )}
      {requestingAccess && <PortalAccessRequestModal initialEmail={email} onClose={() => setRequestingAccess(false)} />}
    </AuthLayout>
  )
}
